import Loadable from 'components/Loadable';
import React from 'react';
import { useControllerState } from 'solox';
import { type VideoPlayerType } from 'src/core/video/VideoUtil';
import { userHasVerifEyeGroups } from 'utils/AlarmUtil';
import useRecoverWebRTC from './hooks/useRecoverWebRTC';
import { useSelectedStream } from './hooks/useSelectedStream';
import { type CameraPlayerProps, type PlayerProps } from './types';
const HLSPlayer = Loadable({ loader: () => import('./hls/HLSPlayer') });
const ConnectHLSPlayer = Loadable({ loader: () => import('./hls/ConnectHLSPlayer') });
const WebRTCPlayer = Loadable({ loader: () => import('./rtspSimpleServerPlayer/WebRTCPlayer') });

const VIDEO_PLAYERS: { [key in VideoPlayerType]: React.FC<PlayerProps> } = {
  HLS: HLSPlayer,
  ConnectHLS: ConnectHLSPlayer,
  WebRTC: WebRTCPlayer,
};

/**
 * Renders a <video> element and plays video from the given camera.
 */
const CameraPlayer: React.FC<CameraPlayerProps> = (props) => {
  const {
    playbackController,
    playingCamera,
    videoPlayerType: overrideVideoPlayerType,
    user,
  } = props;

  const selectedVideoPlayerType =
    useControllerState(
      playbackController.state,
      (state) => state.byPlayerId[playingCamera.playerId]?.state.playerType,
    ) || 'HLS';

  const Player = VIDEO_PLAYERS[overrideVideoPlayerType ?? selectedVideoPlayerType];

  const selectedStream = useSelectedStream(playbackController, playingCamera.playerId);

  const playingLive = useControllerState(playbackController.state, () =>
    playbackController.isPlayingLive(),
  );

  useRecoverWebRTC(props, selectedStream, playingLive, selectedVideoPlayerType);

  // VerifEye 3 users skip authentication and use camera.liveUrl directly
  const overrideWhepURL = userHasVerifEyeGroups(user);

  return (
    <>
      {selectedStream && (
        <Player
          {...props}
          poster={props.poster}
          selectedStream={selectedStream}
          videoPlayerType={selectedVideoPlayerType}
          overrideWhepURL={overrideWhepURL}
        />
      )}
    </>
  );
};

export default React.memo(CameraPlayer);
